import type {
  ClearanceCategory,
  ClearanceHistoryEvent,
  ClearanceHistoryRecord,
  ClearanceHistoryResponse,
} from '../api';
import { cardDate, recordStatusLabel } from './worker-card';

/**
 * Журнал изменений допусков работника и список его записей вместе с
 * аннулированными (issue #525).
 *
 * ЗДЕСЬ ТОЛЬКО ОФОРМЛЕНИЕ. Что произошло, кто это сделал, когда и что на что
 * поменялось — сказал сервер (`GET /clearances/worker/:n/history`,
 * `apps/api/src/clearances/clearance-journal.ts`). Панель ничего не выводит
 * сама: вычисли она «было → стало» по соседним записям, в журнале появился бы
 * второй рассказ о том же событии, и разошлись бы они молча.
 *
 * Отдельным модулем от разметки, потому что проверяется: компонентных тестов
 * в окружении панели нет, а журнал — это то, по чему разбирают, кто внёс
 * неверный допуск.
 */

/** Одно событие журнала так, как его читает человек. */
export type HistoryLine = {
  id: string;
  /** Что сделали: «Ввод», «Продление», «Исправление», «Аннулирование». */
  action: string;
  /** Какой допуск. */
  typeName: string;
  /** Кто: ФИО и табельный. */
  actor: string;
  /** Дата ДОКУМЕНТА-основания, `ДД.ММ.ГГГГ`. */
  documentDate: string;
  /** Время ВВОДА записи в систему, `ДД.ММ.ГГГГ, ЧЧ:ММ`. */
  enteredAt: string;
  /** «было → стало», по строке на изменённое поле. */
  changes: string[];
  /** Причина — у исправления и аннулирования. */
  reason: string | null;
};

/**
 * Действия журнала словами.
 *
 * `CLEARANCE_DELETED` остался в перечне, хотя ручки удаления больше нет: в
 * журнале старой организации такие события лежат, и показать их надо тем, чем
 * они были, а не спрятать.
 */
const ACTIONS: Record<string, string> = {
  CLEARANCE_CREATED: 'Ввод',
  CLEARANCE_RENEWED: 'Продление',
  CLEARANCE_UPDATED: 'Исправление',
  // Issue #537: номер и дата документа впервые вписаны в запись, внесённую до
  // того, как они стали обязательными.
  CLEARANCE_BASIS_COMPLETED: 'Дозаполнение основания',
  CLEARANCE_ANNULLED: 'Аннулирование',
  CLEARANCE_DELETED: 'Удаление (прежний порядок)',
};

export function actionLabel(eventKind: string): string {
  // Неизвестное событие называется как есть, а не прячется: журнал, молча
  // теряющий строки, отвечает на вопрос «что было» неправдой.
  return ACTIONS[eventKind] ?? eventKind;
}

/** Поля записи допуска словами работы, а не именами из ответа сервера. */
const FIELDS: Record<string, string> = {
  documentNumber: 'номер документа',
  // Issue #537: дата документа и начало действия — разные поля. У записи,
  // внесённой по правилам, они совпадают; у записи до #537 — нет.
  documentDate: 'дата документа',
  issuedAt: 'начало действия',
  expiresAt: 'срок действия',
  issuedBy: 'кто выдал',
  electricalSafetyGroup: 'группа по электробезопасности',
  notes: 'примечание',
  annulledAt: 'отметка аннулирования',
};

/** Поля, значение которых — календарная дата, а не текст. */
const DATE_FIELDS = new Set(['documentDate', 'issuedAt', 'expiresAt']);

export function fieldLabel(field: string): string {
  return FIELDS[field] ?? field;
}

/** `2026-09-29T03:14:07.000Z` → `29.09.2026, 03:14` временем того, кто смотрит. */
export function momentText(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${pad(at.getDate())}.${pad(at.getMonth() + 1)}.${at.getFullYear()}`
    + `, ${pad(at.getHours())}:${pad(at.getMinutes())}`
  );
}

/**
 * «было → стало» одной строкой. Пустое значение — прочерк: строка «номер
 * документа: → 12/7» читалась бы как оборванная.
 */
export function changeText(change: { field: string; from: string | null; to: string | null }): string {
  const show = (value: string | null) => {
    if (value === null || value.length === 0) return '—';
    if (DATE_FIELDS.has(change.field)) return cardDate(value);
    if (change.field === 'annulledAt') return momentText(value);
    return value;
  };
  return `${fieldLabel(change.field)}: ${show(change.from)} → ${show(change.to)}`;
}

/** Кто внёс: ФИО и табельный. Обе части необязательны — у легаси-событий их нет. */
export function actorText(event: Pick<ClearanceHistoryEvent, 'actorName' | 'actorEmployeeNumber'>): string {
  const parts = [event.actorName, event.actorEmployeeNumber ? `таб. № ${event.actorEmployeeNumber}` : null].filter(
    (part): part is string => part !== null && part.length > 0,
  );
  return parts.length > 0 ? parts.join(' · ') : 'кто внёс — в журнале не записано';
}

/**
 * Все события журнала работника — от новых к старым, одним списком.
 *
 * `orphanEvents` идут туда же: событие, у которого не осталось живой записи,
 * — это тоже ответ на вопрос «что было с допусками этого человека», и
 * выбросить его значило бы соврать умолчанием.
 */
export function historyLines(response: ClearanceHistoryResponse): HistoryLine[] {
  const events: ClearanceHistoryEvent[] = [
    ...response.clearances.flatMap((record) => record.events),
    ...response.orphanEvents,
  ];
  // ISO-8601 в UTC сравнивается как строка, и порядок получается тот же, что
  // по моменту. Разбор в `Date` ради сортировки ничего бы не добавил.
  events.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  return events.map((event) => ({
    id: event.id,
    action: actionLabel(event.eventKind),
    typeName: event.clearanceTypeName ?? 'вид допуска не назван',
    actor: actorText(event),
    documentDate: cardDate(event.documentDate),
    enteredAt: momentText(event.occurredAt),
    changes: event.changes.map(changeText),
    reason: event.reason,
  }));
}

/** Одна запись допуска в списке «Все записи» — включая аннулированные. */
export type RecordLine = {
  id: string;
  typeName: string;
  /** Категория записи: по ней форма исправления решает, спрашивать ли группу. */
  category: ClearanceCategory | undefined;
  documentNumber: string | null;
  /** Дата документа, `ДД.ММ.ГГГГ`; `null` — не заполнена (запись до #537). */
  documentDate: string | null;
  /** Начало действия допуска, `ДД.ММ.ГГГГ`. */
  issuedAt: string;
  /**
   * Issue #537: у записи нет документа-основания — пуст номер или дата
   * документа. Такую запись не исправляют, а дозаполняют.
   */
  basisMissing: boolean;
  /** Срок действия, `ДД.ММ.ГГГГ`; `null` — бессрочно. */
  expiresAt: string | null;
  /** Группа по электробезопасности римской цифрой, если она в записи есть. */
  group: string | null;
  /** Состояние словами: «действует», «истекает», «просрочен» — или «аннулирован». */
  status: string;
  annulled: boolean;
  /**
   * Issue #543: откуда запись инструктажа — «по журналу инструктажа» или
   * «внесено вручную». Только у инструктажей: у медосмотра и группы по ЭБ
   * другого пути, кроме ручного ввода, нет, и пометка там ничего не сказала бы.
   */
  sourceLabel: string | null;
  annulmentReason: string | null;
  /** Когда аннулировали, `ДД.ММ.ГГГГ, ЧЧ:ММ`; `null` — запись действующая. */
  annulledAt: string | null;
  /** Сама запись — её правит исправление и её аннулируют. */
  record: ClearanceHistoryRecord;
};

/**
 * Откуда запись инструктажа (issue #543). Первичный, повторный и внеплановый
 * проводит мастер в приложении, и запись допуска создаёт журнал; внесённая
 * руками — старые данные, проведённые до перехода на приложение.
 */
export function recordSourceLabel(record: Pick<ClearanceHistoryRecord, 'source' | 'clearanceType'>): string | null {
  if (record.clearanceType?.category !== 'BRIEFING') return null;
  if (record.source === 'BRIEFING_JOURNAL') return 'по журналу инструктажа';
  if (record.source === 'MANUAL') return 'внесено вручную';
  return null;
}

/**
 * Все записи работника, новые сверху, вместе с аннулированными.
 *
 * АННУЛИРОВАННАЯ ЗАПИСЬ НЕ ПОКАЗЫВАЕТ СВОЙ СРОК СЛОВОМ «ДЕЙСТВУЕТ».
 * `computedStatus` у неё остаётся тем же, каким был, — сервер считает его по
 * датам и аннулирования не касается, потому что в светофор такая запись не
 * попадает вовсе. Здесь она попадает на экран, и слово «действует» рядом с
 * аннулированным документом прочли бы как допуск.
 */
export function recordLines(response: ClearanceHistoryResponse): RecordLine[] {
  return [...response.clearances]
    .sort((a, b) => b.issuedAt.localeCompare(a.issuedAt))
    .map((record) => ({
      id: record.id,
      typeName: record.clearanceType?.name ?? 'вид допуска не назван',
      category: record.clearanceType?.category,
      documentNumber: record.documentNumber ?? null,
      documentDate: record.documentDate ? cardDate(record.documentDate) : null,
      issuedAt: cardDate(record.issuedAt),
      basisMissing: record.basisMissing ?? false,
      expiresAt: record.expiresAt ? cardDate(record.expiresAt) : null,
      group: record.electricalSafetyGroup ?? null,
      status: record.annulledAt ? 'аннулирован' : recordStatusLabel(record.computedStatus),
      annulled: record.annulledAt != null,
      sourceLabel: recordSourceLabel(record),
      annulmentReason: record.annulmentReason ?? null,
      annulledAt: record.annulledAt ? momentText(record.annulledAt) : null,
      record,
    }));
}
